import { Link as RouterLink, redirect } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { AbsoluteCenter, Box, Heading, HStack, Icon, Link, Stack, Text } from "@chakra-ui/react";

import { RoutesMap } from "../../routes.tsx";
import { usePersistStore } from "../../store/persistStore.ts";
import { PinForm } from "../../components/features/LoginPage/PinForm";
import { usePinFormState } from "../../components/features/LoginPage/PinForm/hooks";
import { clearAuthorization } from "../../utils/session/clearAuthorization";
import { isNewAuthorization, useAuthorizationStore } from "../../store/authorizationStore";

type PinMode = "enter" | "create" | null;

function getKeycloakPinMode(auth: ReturnType<typeof useAuthorizationStore.getState>): PinMode {
  if (auth.authSessionId) {
    if (auth.flow === "REG" && !auth.passportChecked) return null;
    if (auth.restoringPin) return "create";
    if (auth.flow === "AUTH") return "enter";
    if (auth.passportChecked) return "create";
  }

  if (auth.deviceId && !auth.restoringPin && !auth.authSessionId) return "enter";

  return null;
}

export function PinPage() {
  const { setPin } = usePinFormState();

  const auth = useAuthorizationStore();
  const deviceJWT = usePersistStore(state => state.deviceJWT);
  const enteringPin = auth.version === "keycloak" ? getKeycloakPinMode(auth) === "enter" : !!deviceJWT;
  const clearJwt = usePersistStore(state => state.clearJwt);
  const clearToken = usePersistStore(state => state.clearDeviceJwt);

  const onForget = () => {
    if (isNewAuthorization()) {
      clearAuthorization("keycloak", true);
      useAuthorizationStore.setState({ restoringPin: true });
      return;
    }

    clearJwt();
    clearToken();
    setPin("");
  };

  return (
    <Box position="relative" h="calc(100vh - 84px * 2)">
      <AbsoluteCenter axis="both">
        <Stack spacing={9} minW="300px" pb="130px">
          <div>
            <Heading mb={2} textAlign="center">{enteringPin ? "Введите код" : "Придумайте код"}</Heading>
            <Text size="sm" align="center">
              Для быстрого входа в кабинет
            </Text>
          </div>
          <PinForm />

          {enteringPin && (
            <Box>
              <HStack alignItems="center" justifyContent="center">
                <Link onClick={onForget} as={RouterLink} to={RoutesMap.login.index} color="secondary">
                  Не помню код
                </Link>
                <Icon color="secondary" boxSize={4} as={ChevronRightIcon} />
              </HStack>
            </Box>
          )}
        </Stack>
      </AbsoluteCenter>
    </Box>
  );
}

PinPage.loader = () => {
  if (isNewAuthorization()) {
    const auth = useAuthorizationStore.getState();
    return getKeycloakPinMode(auth) !== null ? null : redirect("/login");
  }

  const { jwt, deviceJWT } = usePersistStore.getState();
  if (!jwt && !deviceJWT) {
    return redirect(RoutesMap.login.index);
  }
  return null;
};
